import { LIVE } from "@/constants/testIds";
import { Sheet } from "./GoLivePrimitives";

export const FILTERS = [
  { key: "none", label: "Normal", css: "none" },
  { key: "warm", label: "Warm", css: "saturate(1.2) contrast(1.05) sepia(0.15)" },
  { key: "cool", label: "Cool", css: "saturate(1.15) hue-rotate(-8deg) brightness(1.05)" },
  { key: "noir", label: "Noir", css: "grayscale(1) contrast(1.1)" },
  { key: "vivid", label: "Vivid", css: "saturate(1.6) contrast(1.1)" },
  { key: "dream", label: "Dream", css: "blur(0.3px) brightness(1.08) saturate(1.15)" },
];

const GIFT_KEYS = ["rose", "fire", "kiss", "diamond", "bottle", "cake", "crown", "rocket", "lambo", "yacht"];
const EXTENSIONS = ["Chatbot Mod", "Voice Effects", "Confetti Cannon", "Auto Captions", "Guest Queue"];

export default function GoLiveSheets({
  sheet, setSheet,
  filter, setFilter,
  paidEntry, setPaidEntry,
  customGifts, setCustomGifts,
  poll, setPoll,
  goalCoins, setGoalCoins,
  wheel, setWheel,
  extensions, setExtensions,
}) {
  return (
    <>
      <Sheet open={sheet === "filter"} title="Filters" onClose={() => setSheet(null)}>
        <div className="grid grid-cols-3 gap-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`p-3 rounded-xl border text-sm text-white ${
                filter === f.key ? "border-yellow-400 bg-yellow-400/10" : "border-white/10 bg-white/5"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Sheet>

      <Sheet open={sheet === "paid"} title="Paid Live entry" onClose={() => setSheet(null)} onSave={() => setSheet(null)}>
        <p className="text-white/60 text-sm mb-3">
          Charge viewers coins to join your stream. Set 0 for free entry.
        </p>
        <input
          data-testid={LIVE.paidLiveInput}
          type="number"
          min="0"
          value={paidEntry}
          onChange={(e) => setPaidEntry(parseInt(e.target.value || "0", 10))}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white"
          placeholder="Entry cost in coins"
        />
      </Sheet>

      <Sheet open={sheet === "gifts"} title="Custom gifts" onClose={() => setSheet(null)} onSave={() => setSheet(null)}>
        <p className="text-white/60 text-sm mb-3">
          Choose which gifts appear in your live gift picker.
        </p>
        <div className="grid grid-cols-4 gap-2">
          {GIFT_KEYS.map((g) => {
            const on = customGifts.includes(g);
            return (
              <button
                key={g}
                onClick={() =>
                  setCustomGifts((cur) => (on ? cur.filter((x) => x !== g) : [...cur, g]))
                }
                className={`py-2 rounded-lg text-sm capitalize border ${
                  on ? "bg-yellow-400 text-black border-yellow-300" : "bg-white/5 text-white border-white/10"
                }`}
              >
                {g}
              </button>
            );
          })}
        </div>
      </Sheet>

      <Sheet open={sheet === "poll"} title="Create a poll" onClose={() => setSheet(null)} onSave={() => setSheet(null)}>
        <input
          data-testid={LIVE.pollQuestion}
          value={poll.question}
          onChange={(e) => setPoll((p) => ({ ...p, question: e.target.value }))}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white mb-3"
          placeholder="Ask your viewers…"
        />
        {poll.options.map((opt, i) => (
          <input
            key={i}
            data-testid={LIVE.pollOption(i)}
            value={opt}
            onChange={(e) =>
              setPoll((p) => {
                const options = [...p.options];
                options[i] = e.target.value;
                return { ...p, options };
              })
            }
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white mb-2"
            placeholder={`Option ${i + 1}`}
          />
        ))}
        <button
          onClick={() => setPoll((p) => ({ ...p, options: [...p.options, ""] }))}
          className="text-yellow-400 text-sm"
        >
          + Add option
        </button>
      </Sheet>

      <Sheet open={sheet === "goal"} title="Stream goal" onClose={() => setSheet(null)} onSave={() => setSheet(null)}>
        <p className="text-white/60 text-sm mb-3">
          Set a coin goal — a progress bar will show viewers how close you are.
        </p>
        <input
          data-testid={LIVE.goalInput}
          type="number"
          min="0"
          value={goalCoins}
          onChange={(e) => setGoalCoins(parseInt(e.target.value || "0", 10))}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white"
          placeholder="Goal in coins"
        />
      </Sheet>

      <Sheet open={sheet === "wheel"} title="Prize wheel" onClose={() => setSheet(null)} onSave={() => setSheet(null)}>
        <p className="text-white/60 text-sm mb-3">
          Configure segments viewers can win when they spin.
        </p>
        {wheel.map((w, i) => (
          <input
            key={i}
            data-testid={LIVE.wheelSegment(i)}
            value={w}
            onChange={(e) =>
              setWheel((cur) => {
                const next = [...cur];
                next[i] = e.target.value;
                return next;
              })
            }
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white mb-2"
            placeholder={`Segment ${i + 1}`}
          />
        ))}
        <button onClick={() => setWheel((cur) => [...cur, ""])} className="text-yellow-400 text-sm">
          + Add segment
        </button>
      </Sheet>

      <Sheet open={sheet === "ext"} title="Extensions" onClose={() => setSheet(null)} onSave={() => setSheet(null)}>
        <p className="text-white/60 text-sm mb-3">Toggle add-ons that run during your stream.</p>
        <div className="space-y-2">
          {EXTENSIONS.map((ext) => {
            const on = extensions.includes(ext);
            return (
              <button
                key={ext}
                onClick={() =>
                  setExtensions((cur) => (on ? cur.filter((x) => x !== ext) : [...cur, ext]))
                }
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                  on ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10 text-white"
                }`}
              >
                <span>{ext}</span>
                <span className="text-xs">{on ? "On" : "Off"}</span>
              </button>
            );
          })}
        </div>
      </Sheet>
    </>
  );
}
